import { Table as TableType } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetAllSuppliers,
  useGetSuppliers,
} from '@/modules/workforce/Crew/apiHooks';
import useCrewHook from '@/modules/workforce/Crew/components/Crews/logic';
import PrintToolBar from '@/modules/workforce/Crew/components/Crews/printToolBar';
import { ISuppliers } from '@/modules/workforce/Crew/types';
import { InputField, Table } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { EmptyCard, SingleQrCode } from '@/shared/components/molecules';
import QrCodeModal from '@/shared/components/molecules/QrCodeModal.tsx';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useDebounce from '@/shared/hooks/useDebounce';

import Loading from '~/images/shared/Loading.svg';
import SearchIcon from '~/images/shared/search.svg';
import suppliersEmptyState from '~/images/shared/suppliersEmptyState.png';

const PER_PAGE = 10;

// TODO: refactor the filter, opening modals, and ref
const CrewTable = () => {
  const tableRef = useRef<TableType<ISuppliers.ISupplier>>(null);
  const qrcodeRef = useRef(null);

  const { user } = useAuth();
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);

  const { push } = useRouter();
  const { openModal } = useModal();
  const { t } = useTranslation('common');
  const { suppliersTableColumns } = useCrewHook();

  const [page, setPage] = useState(1);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [openQrCodeModal, setOpenQrCodeModal] = useState(false);
  const [activeQrData, setActiveQrData] = useState<null | ISuppliers.ISupplier>(
    null
  );

  const [filter, setFilter] = useState<{
    search: string;
    selectedRows: ISuppliers.ISupplier[];
    allpagesData: ISuppliers.ISupplier[];

    isSelectAll: boolean;
  }>({
    search: '',
    selectedRows: [],
    isSelectAll: false,
    allpagesData: [],
    // advancedFilter: {},
  });

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  useEffect(() => {
    setFilter((prev) => ({ ...prev, search }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  function onClearTableSelection() {
    tableRef.current?.resetRowSelection();
    setFilter((prev) => ({
      ...prev,
      allpagesData: [],
    }));
  }

  const onPageChange = (page: number) => {
    setPage(page);
  };

  const handleOpenDemoCTA = (fn?: () => void) => {
    if (isDemoFarm) openModal(<DemoCTA />);
    else if (fn) fn();
  };

  const { data, isLoading } = useGetSuppliers({
    pageNumber: page,
    pageSize: PER_PAGE,
    searchQuery: search,
    todayDate: format(new Date(), 'yyyy-MM-dd'),
    isActive: true,
  });

  // GET all supplier pages when choose select all suppliers
  const { data: allSuppliersData, isSuccess } = useGetAllSuppliers({
    trigger: selectAllPages,
    todayDate: format(new Date(), 'yyyy-MM-dd'),
  });

  // when select get all supplier in all pages set all suppliers returned data to be printed
  useEffect(() => {
    if (isSuccess && selectAllPages) {
      setFilter((prev) => ({
        ...prev,
        allpagesData: allSuppliersData?.suppliers,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, selectAllPages]);

  // when change selection and you were chosen select all then reset select all and all pages data
  useEffect(() => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setFilter((prev) => ({
        ...prev,
        allpagesData: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.selectedRows]);

  // qr code methods
  const onClickQrHandler = (row: ISuppliers.ISupplier) => {
    setActiveQrData(row);

    setOpenQrCodeModal(true);

    //
  };
  const closeQrModalHandler = () => {
    //
    setOpenQrCodeModal(false);
  };

  if (isLoading) {
    return (
      <div className='mt-100'>
        <Loading />
      </div>
    );
  }
  // if no data found
  if (!data || data?.suppliers?.length === 0) {
    return (
      <EmptyCard
        classeName='my-32'
        variant='image'
        vector={suppliersEmptyState}
        message={t('no-suppiers')}
        directiveMessage={t('no-suppiers-directive')}
      />
    );
  }
  return (
    <div>
      <section>
        <div className='gap-24 tablet:flex'>
          <div className='flex-grow' onClick={() => handleOpenDemoCTA()}>
            <InputField
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchIcon />}
              placeholder={t('search')}
              disabled={isDemoFarm}
            />
          </div>

          <div className='hidden'>
            <div
              ref={qrcodeRef}
              className='block print:break-inside-avoid-page'
            >
              <div className='grid grid-cols-3  gap-y-72'>
                {filter.selectedRows.map((row) => (
                  <SingleQrCode
                    key={row.supplierId}
                    variant='multiple'
                    qrData={{
                      qrCode: row?.qrCode,
                      name: row.supplierName,
                      title: t('team_leader'),
                      logo: user?.companyLogo,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {!!filter.selectedRows.length && (
          <PrintToolBar
            qrcodeRef={qrcodeRef}
            isPrintDisabled={filter.selectedRows?.length === 0 || isDemoFarm}
            rowsLength={filter.selectedRows.length}
            onClearTableSelection={onClearTableSelection}
          />
        )}
      </section>
      <div className='mt-11'>
        <Table<ISuppliers.ISupplier>
          tableRef={tableRef}
          tableWrapperClassName='!overflow-visible'
          tableClassName='!border-spacing-y-[2px]'
          headCellClassName='ltr:text-left rtl:text-right text-surface-10'
          rowContainerClassName='cursor-pointer'
          columns={suppliersTableColumns(t, onClickQrHandler)}
          data={data?.suppliers || []}
          pagination={true}
          getIsSelectAll={(isSelectAll) =>
            setFilter((prev) => ({ ...prev, isSelectAll }))
          }
          getSelectedRows={(rows) => {
            setFilter((prev) => ({ ...prev, selectedRows: rows }));
          }}
          currentPage={page}
          perPage={PER_PAGE}
          totalCount={data?.totalCount || 0}
          onPageChange={onPageChange}
          onClickRow={(row) =>
            handleOpenDemoCTA(() =>
              push(`${routes.suppliers}/${row?.supplierId?.toString()}`)
            )
          }
        />
        <QrCodeModal
          qrData={{
            qrCode: activeQrData?.qrCode,
            name: activeQrData?.supplierName,
            title: t('team_leader'),
            logo: user?.companyLogo,
          }}
          open={openQrCodeModal}
          closeModal={closeQrModalHandler}
        />
      </div>
    </div>
  );
};

export { CrewTable };
